import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, Send } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { VendorPurchaseOrder, NonVendorPurchaseOrder, SkippedPurchaseOrder } from "src/types";

interface ModalProps {
  onClose?: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

interface ConfirmVendorPurchaseOrdersModalProps {
  purchaseOrders: VendorPurchaseOrder[];
  onConfirm: () => void;
  onCancel: () => void;
  errorMessage?: string;
  skippedPurchaseOrders: SkippedPurchaseOrder[];
}

interface SuccessModalProps extends ModalProps {
  vendorPurchaseOrders: VendorPurchaseOrder[];
  nonVendorPurchaseOrders: NonVendorPurchaseOrder[];
  skippedPurchaseOrders: SkippedPurchaseOrder[];
  totalPOs: number;
}

interface SuccessPOSentModalProps extends ModalProps {
  totalPOs: number;
  successfulPOs: number;
  failedPOs: number;
  failedPOsList: Array<{
    id: string;
    vendorName: string;
    error: string;
  }>;
  nonVendorPurchaseOrders: NonVendorPurchaseOrder[];
  skippedPurchaseOrders: SkippedPurchaseOrder[];
}

const Success: React.FC<SuccessModalProps> = ({
  vendorPurchaseOrders,
  nonVendorPurchaseOrders,
  skippedPurchaseOrders,
  totalPOs,
  onClose
}) => {
  const hasSkipped = skippedPurchaseOrders.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        <div className="overflow-y-auto max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-6 h-6 text-success-600" />
              <h2 className="text-lg font-semibold text-gray-900">Purchase Orders Uploaded</h2>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                &times;
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            {/* Success Message */}
            <div className="text-center pb-2">
              <p className="text-sm text-gray-700">
                {hasSkipped
                  ? "Some purchase orders were skipped because they already exist with an active status"
                  : "The purchase orders have been added to the system"}
              </p>
            </div>

            {/* Stats Summary */}
            <div className={`grid gap-3 ${hasSkipped ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Total POs</p>
                <p className="text-xl font-bold text-gray-900">{totalPOs}</p>
              </div>
              <div className="bg-success-50 rounded-lg p-3 border border-success-200">
                <p className="text-xs text-gray-600 mb-1">Vendor Purchase Orders</p>
                <p className="text-xl font-bold text-success-700">{vendorPurchaseOrders.length}</p>
              </div>
              <div className="bg-warning-50 rounded-lg p-3 border border-warning-200">
                <p className="text-xs text-gray-600 mb-1">Mismatch-Vendor POs</p>
                <p className="text-xl font-bold text-warning-700">{nonVendorPurchaseOrders.length}</p>
              </div>
              {hasSkipped && (
                <div className="bg-error-50 rounded-lg p-3 border border-error-200">
                  <p className="text-xs text-gray-600 mb-1">Skipped</p>
                  <p className="text-xl font-bold text-error-700">{skippedPurchaseOrders.length}</p>
                </div>
              )}
            </div>

            {/* Skipped Purchase Orders Table */}
            {hasSkipped && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                  <XCircle className="w-4 h-4 mr-2 text-error-600" />
                  Skipped Purchase Orders
                </h4>
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Vendor</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {skippedPurchaseOrders.map((po) => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{po.id}</td>
                          <td className="px-3 py-2 text-gray-900">{po.vendorName}</td>
                          <td className="px-3 py-2 text-error-700">{po.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Vendors Not Found Table */}
            {nonVendorPurchaseOrders.length > 0 && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                  <AlertTriangle className="w-4 h-4 mr-2 text-warning-600" />
                  Vendors Not Found
                </h4>
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Vendor Name
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {nonVendorPurchaseOrders.map((po) => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{po.id}</td>
                          <td className="px-3 py-2 text-gray-900">{po.sheetVendorName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Footer Link */}
            <div className="text-center border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600">
                View all purchase orders{" "}
                <Link
                  to={ROUTES_CONFIG.purchaseOrdersDashboard.path}
                  className="text-black font-semibold hover:text-gray-700 underline"
                >
                  here
                </Link>
              </p>
            </div>

            {/* Home Button */}
            <div className="flex justify-center">
              <Link to={ROUTES_CONFIG.home.path}>
                <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                  Home
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessPOSent: React.FC<SuccessPOSentModalProps> = ({
  totalPOs,
  successfulPOs,
  failedPOs,
  failedPOsList,
  nonVendorPurchaseOrders,
  skippedPurchaseOrders,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        <div className="overflow-y-auto max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <Send className="w-6 h-6 text-success-600" />
              <h2 className="text-lg font-semibold text-gray-900">Purchase Orders Sent</h2>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                &times;
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            {/* Stats Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Total</p>
                <p className="text-xl font-bold text-gray-900">{totalPOs}</p>
              </div>
              <div className="bg-success-50 rounded-lg p-3 border border-success-200">
                <p className="text-xs text-gray-600 mb-1">Sent</p>
                <p className="text-xl font-bold text-success-700">{successfulPOs}</p>
              </div>
              {nonVendorPurchaseOrders.length > 0 && (
                <div className="bg-warning-50 rounded-lg p-3 border border-warning-200">
                  <p className="text-xs text-gray-600 mb-1">Mismatch-Vendor POs</p>
                  <p className="text-xl font-bold text-warning-700">{nonVendorPurchaseOrders.length}</p>
                </div>
              )}
              {failedPOs > 0 && (
                <div className="bg-error-50 rounded-lg p-3 border border-error-200">
                  <p className="text-xs text-gray-600 mb-1">Failed</p>
                  <p className="text-xl font-bold text-error-700">{failedPOs}</p>
                </div>
              )}
              {skippedPurchaseOrders.length > 0 && (
                <div className="bg-error-50 rounded-lg p-3 border border-error-200">
                  <p className="text-xs text-gray-600 mb-1">Skipped</p>
                  <p className="text-xl font-bold text-error-700">{skippedPurchaseOrders.length}</p>
                </div>
              )}
            </div>

            {/* Failed POs Table */}
            {failedPOs > 0 && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                  <XCircle className="w-4 h-4 mr-2 text-error-600" />
                  Failed Purchase Orders
                </h4>
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Vendor</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Error</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {failedPOsList.map((po) => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{po.id}</td>
                          <td className="px-3 py-2 text-gray-900">{po.vendorName}</td>
                          <td className="px-3 py-2 text-error-700">{po.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Skipped Purchase Orders Table */}
            {skippedPurchaseOrders.length > 0 && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                  <XCircle className="w-4 h-4 mr-2 text-error-600" />
                  Skipped Purchase Orders
                </h4>
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Vendor</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {skippedPurchaseOrders.map((po) => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{po.id}</td>
                          <td className="px-3 py-2 text-gray-900">{po.vendorName}</td>
                          <td className="px-3 py-2 text-error-700">{po.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Vendors Not Found Table */}
            {nonVendorPurchaseOrders.length > 0 && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                <h4 className="text-xs font-semibold text-gray-900 mb-3 flex items-center uppercase tracking-wide">
                  <AlertTriangle className="w-4 h-4 mr-2 text-warning-600" />
                  Vendors Not Found
                </h4>
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                          Vendor Name
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {nonVendorPurchaseOrders.map((po) => (
                        <tr key={po.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{po.id}</td>
                          <td className="px-3 py-2 text-gray-900">{po.sheetVendorName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Footer Link */}
            <div className="text-center border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600">
                View all purchase orders{" "}
                <Link
                  to={ROUTES_CONFIG.purchaseOrdersDashboard.path}
                  className="text-black font-semibold hover:text-gray-700 underline"
                >
                  here
                </Link>
              </p>
            </div>

            {/* Home Button */}
            <div className="flex justify-center">
              <Link to={ROUTES_CONFIG.home.path}>
                <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                  Home
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmVendorPurchaseOrders: React.FC<ConfirmVendorPurchaseOrdersModalProps> = ({
  purchaseOrders,
  onConfirm,
  onCancel,
  errorMessage,
  skippedPurchaseOrders
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        <div className="overflow-y-auto max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <Send className="w-6 h-6 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Send Purchase Orders to Vendors</h2>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Error Message */}
            {errorMessage && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-3 flex items-start">
                <XCircle className="w-5 h-5 text-error-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error-700">{errorMessage}</p>
              </div>
            )}

            {/* Skipped POs Warning */}
            {skippedPurchaseOrders.length > 0 && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 flex items-start">
                <AlertTriangle className="w-5 h-5 text-warning-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-warning-700">
                  <span className="font-bold">{skippedPurchaseOrders.length}</span> purchase order
                  {skippedPurchaseOrders.length !== 1 ? "s were" : " was"} skipped because{" "}
                  {skippedPurchaseOrders.length !== 1 ? "they" : "it"} already exist
                  {skippedPurchaseOrders.length === 1 ? "s" : ""} with an active status.
                </p>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-success-50 border border-success-200 rounded-lg p-4 flex items-start">
              <CheckCircle2 className="w-5 h-5 text-success-600 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                <span className="font-bold text-gray-900">{purchaseOrders.length}</span> purchase order
                {purchaseOrders.length !== 1 ? "s" : ""} {purchaseOrders.length !== 1 ? "have" : "has"} matching vendors
                and can be automatically sent.
              </p>
            </div>

            {/* Purchase Orders Table */}
            <div className="border border-neutral-200 rounded-lg p-4 bg-white">
              <h4 className="text-xs font-semibold text-gray-900 mb-3 uppercase tracking-wide">Purchase Orders</h4>
              <div className="max-h-64 overflow-y-auto rounded border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">PO ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Vendor</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {purchaseOrders.map((po) => (
                      <tr key={po.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900 font-medium">{po.id}</td>
                        <td className="px-3 py-2 text-gray-900">{po.vendorName}</td>
                        <td className="px-3 py-2 text-gray-600">{po.vendorEmails.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Confirmation Question */}
            <div className="text-center border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-700 font-medium">
                Send these purchase orders to their respective vendors?
              </p>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 bg-gray-50">
            <button
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onConfirm}
            >
              Send Purchase Orders
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Upload Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while uploading purchase orders. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Upload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadPurchaseOrdersModals = {
  Success,
  SuccessPOSent,
  Error,
  ConfirmVendorPurchaseOrders
};

export { UploadPurchaseOrdersModals };
